import { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash-es';

/**
 * Custom hook to manage state that is synchronized with external data (e.g., Redux state)
 * while protecting against cursor jumping issues during debounced updates.
 *
 * This hook solves the problem where:
 * 1. User types in a CodeMirror editor
 * 2. Change is dispatched with debounce
 * 3. Redux state updates
 * 4. Component re-renders with new props
 * 5. Local state is overwritten, causing cursor to jump to end
 *
 * How it works:
 * - Maintains a "lock" during local editing that blocks external updates
 * - Lock is released after a delay (matching debounce time + buffer)
 * - This ensures external updates from the same edit session are ignored
 * - Updates from other sources (e.g., other tabs, undo/redo) still work
 */
export const useDebounceProtectedState = <TExternal, TLocal>(
	externalData: TExternal,
	mapper: (data: TExternal) => TLocal,
	debounceMs = 500
): [TLocal, (updates: Partial<TLocal>) => void] => {
	const [localState, setLocalState] = useState<TLocal>(() => mapper(externalData));
	const isEditingLocallyRef = useRef(false);
	const lockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const mapperRef = useRef(mapper);
	useEffect(() => {
		mapperRef.current = mapper;
	}, [mapper]);

	useEffect(() => {
		// If we're currently editing locally, ignore the external update
		// This prevents cursor jumping from debounced updates
		if (isEditingLocallyRef.current) {
			return;
		}

		// Update local state from external data
		const mappedData = mapperRef.current(externalData);

		// Only update if data actually changed (using deep equality check)
		if (!isEqual(mappedData, localState)) {
			setLocalState(mappedData);
		}
	}, [externalData, localState]);

	const handleLocalChange = useCallback(
		(updates: Partial<TLocal>) => {
			// Set the lock flag
			isEditingLocallyRef.current = true;

			// Update local state immediately
			setLocalState((prev) => ({ ...prev, ...updates }));

			// Clear any existing timeout
			if (lockTimeoutRef.current) {
				clearTimeout(lockTimeoutRef.current);
			}

			// Release the lock after debounce time + buffer to allow Redux update to propagate
			const LOCK_RELEASE_BUFFER_MS = 100;
			lockTimeoutRef.current = setTimeout(() => {
				isEditingLocallyRef.current = false;
			}, debounceMs + LOCK_RELEASE_BUFFER_MS);
		},
		[debounceMs]
	);

	// Cleanup timeout on unmount
	useEffect(() => () => clearTimeout(lockTimeoutRef.current), []);

	return [localState, handleLocalChange] as const;
};
